import { ChangeEvent, useCallback } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect } from 'react-redux';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import {
  getJudgmentActCancellationInfoItemExplanationBlockId,
  getJudgmentActCancellationInfoItemInfoBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormJudgmentActsAndDecisions } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormJudgmentActsAndDecisions';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';

import { JudgmentCancellationInformationTypesEnum } from '../../../CorePackage/CatalogsModule/enums/JudgmentCancellationInformationTypesEnum';
import { IKnmJudgmentCancellationInfoSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmJudgmentCancellationInfoSelectOption';
import { getJudgmentCancellationInfoSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { Checkbox } from '../../../UIPackage/MainModule/components/Checkbox';
import { CloseButton } from '../../../UIPackage/MainModule/components/CloseButton';
import { TextareaField } from '../../../UIPackage/MainModule/components/TextareaField';
import styles from './Judgment.module.scss';

interface IDecisionJudgmentDecisionInfoProps {
  watchOnly: boolean;
  judgmentItemName: string;
  judgmentItemSubjectDataItemName: string;
  judgmentItemSubjectDataItemId: string;
  formRef: IDecisionFormRef | undefined;
}

interface IDecisionJudgmentDecisionInfoStoreProps {
  cancellationInformationOptions: IKnmJudgmentCancellationInfoSelectOption[] | undefined;
}

const DecisionJudgmentDecisionInfoWithoutStore = ({
  watchOnly,
  formRef,
  judgmentItemName,
  cancellationInformationOptions,
  judgmentItemSubjectDataItemName,
  judgmentItemSubjectDataItemId,
}: IDecisionJudgmentDecisionInfoProps & IDecisionJudgmentDecisionInfoStoreProps) => {
  const remove = formRef?.current.mutators.remove;
  const makeRemoveCancellationInformationHandler = useCallback(
    (index: number) => {
      !watchOnly && remove && remove(`${judgmentItemSubjectDataItemName}.cancellationActsAndDecisions`, index);
    },
    [formRef, judgmentItemName, judgmentItemSubjectDataItemName],
  );

  const handleCancellationInformationCheckboxChange = useCallback(
    (v: ChangeEvent<HTMLInputElement>) => {
      if (v.target.checked) {
        formRef?.current.change(
          `${v.target.name}` as keyof IDecisionForm,
          // @ts-ignore
          cancellationInformationOptions?.filter(
            x => x.label === JudgmentCancellationInformationTypesEnum.PARTIAL_CANCELLATION,
          )[0],
        );
      } else {
        formRef?.current.change(`${v.target.name}` as keyof IDecisionForm);
      }
    },
    [formRef, cancellationInformationOptions],
  );

  return (
    <table width="100%" className={styles.Table}>
      <thead className={styles.TableHeader}>
        <tr>
          <th className={styles.TableInformationTitle}>№</th>
          <th className={styles.TableInformationTitle}>РЕШЕНИЕ</th>
          <th className={styles.TableInformationTitle}>ОТМЕНЕНО ЧАСТИЧНО</th>
          <th className={styles.TableInformationTitle}>ПОЯСНЕНИЕ</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <FieldArray name={`${judgmentItemSubjectDataItemName}.cancellationActsAndDecisions`}>
          {({ fields }: FieldArrayRenderProps<IKnmErknmFormJudgmentActsAndDecisions, HTMLElement>) => {
            if (!fields.length) {
              return null;
            }

            return fields.map((actCancellationInformationName, actCancellationInformationIndex) => {
              const actCancellationInformationValue: IKnmErknmFormJudgmentActsAndDecisions =
                fields.value?.[actCancellationInformationIndex];

              return (
                <tr key={actCancellationInformationName}>
                  <td className={styles.TableInformationText}>{actCancellationInformationIndex + 1}</td>
                  <td className={styles.TableInformationText}>{actCancellationInformationValue.title}</td>
                  <td
                    id={getJudgmentActCancellationInfoItemInfoBlockId(
                      judgmentItemSubjectDataItemId,
                      actCancellationInformationValue.listId,
                    )}
                  >
                    <Checkbox
                      name={`${actCancellationInformationName}.cancellationInformation`}
                      theme="system"
                      onChange={handleCancellationInformationCheckboxChange}
                      checked={
                        actCancellationInformationValue.cancellationInformation?.label ===
                        JudgmentCancellationInformationTypesEnum.PARTIAL_CANCELLATION
                      }
                      disabled={watchOnly}
                    />
                  </td>
                  <td
                    id={getJudgmentActCancellationInfoItemExplanationBlockId(
                      judgmentItemSubjectDataItemId,
                      actCancellationInformationValue.listId,
                    )}
                    className={styles.TableInformationText}
                  >
                    <TextareaField
                      name={`${actCancellationInformationName}.explanation`}
                      disabled={watchOnly}
                      placeholder="Введите пояснение"
                    />
                  </td>
                  <td className={styles.TableInformationText}>
                    {!watchOnly && (
                      <CloseButton
                        className="shared-deleting-row-btn"
                        onClick={() => makeRemoveCancellationInformationHandler(actCancellationInformationIndex)}
                      />
                    )}
                  </td>
                </tr>
              );
            });
          }}
        </FieldArray>
      </tbody>
    </table>
  );
};

const mapStateToProps = (state: IRootState): IDecisionJudgmentDecisionInfoStoreProps => ({
  cancellationInformationOptions: getJudgmentCancellationInfoSelectOptions(state),
});

export const DecisionJudgmentDecisionInfo = connect(mapStateToProps)(DecisionJudgmentDecisionInfoWithoutStore);
